import {
  PayrollAnchorField,
  PayrollFileColumnMapping,
  PayrollFileColumnTransformation,
  PayrollFileColumnTransformationColumn,
} from '@api/lib/payrollProcessing/types';
import {ExtractedFile, FileForUpload} from '@client/pages/payroll/types';
import {getTransformationColumnsFromMapping} from '@client/pages/payroll/utils/transformationUtils';
import {PayrollTargetField} from '@api/lib/payrollProcessing/constants';

export function removeInvalidColumnsFromMapping(
  existingMappings: PayrollFileColumnMapping[],
  extractedFiles: ExtractedFile[],
): {
  /** Mappings that were completely removed because referenced columns are missing */
  removed: RemovedMappingInfo[];
  /** Mappings that were retained but with some columns pruned (for multi-column transformations) */
  pruned: PrunedMappingInfo[];
  /** All mappings that were retained (including those with pruned columns) */
  retained: PayrollFileColumnMapping[];
} {
  if (existingMappings.length === 0 || extractedFiles.length === 0) {
    return {removed: [], pruned: [], retained: existingMappings};
  }

  const removed: RemovedMappingInfo[] = [];
  const pruned: PrunedMappingInfo[] = [];
  const retained: PayrollFileColumnMapping[] = [];

  for (const existingMapping of existingMappings) {
    if (!existingMapping.columnTransformation) {
      removed.push({
        targetField: existingMapping.targetField,
        missingColumns: [],
      });
      continue;
    }

    const transformationType = existingMapping.columnTransformation.type;

    // Check if this is a multi-column transformation that supports partial removal
    const isPartialMultiColumnTransformation = [
      'COLUMN_ADDITION',
      'COLUMN_AVERAGE',
      'COLUMN_MAXIMUM',
      'COLUMN_MINIMUM',
    ].includes(transformationType);

    if (isPartialMultiColumnTransformation && 'columns' in existingMapping.columnTransformation) {
      // For multi-column transformations, filter out missing columns instead of removing the entire mapping
      const columns = existingMapping.columnTransformation.columns;
      const missingColumns: {fileId: string; columnName: string}[] = [];
      const validColumns: PayrollFileColumnTransformationColumn[] = [];

      for (const column of columns) {
        if (!column) continue;

        // Check if the primary column exists
        const primaryExists = columnExistsInExtractedFiles(column, extractedFiles);

        if (!primaryExists) {
          missingColumns.push({fileId: column.fileId, columnName: column.name});
          // Don't add to validColumns - this column is missing
        } else {
          // Primary column exists - also filter fallback columns if any
          const validFallbacks =
            column.fallbackColumns?.filter((fb) =>
              columnExistsInExtractedFiles(fb, extractedFiles),
            ) || [];

          // Track missing fallbacks (for reporting purposes, but don't fail the whole column)
          column.fallbackColumns?.forEach((fb) => {
            if (!columnExistsInExtractedFiles(fb, extractedFiles)) {
              missingColumns.push({fileId: fb.fileId, columnName: fb.name});
            }
          });

          validColumns.push({
            ...column,
            fallbackColumns: validFallbacks.length > 0 ? validFallbacks : undefined,
          });
        }
      }

      if (validColumns.length >= 2) {
        // We have enough columns - retain the mapping with pruned columns
        const updatedTransformation = {
          ...existingMapping.columnTransformation,
          columns: validColumns,
        } as PayrollFileColumnTransformation;

        const updatedMapping: PayrollFileColumnMapping = {
          ...existingMapping,
          columnTransformation: updatedTransformation,
        };

        retained.push(updatedMapping);

        // If any columns were pruned, report them separately
        if (missingColumns.length > 0) {
          pruned.push({
            targetField: existingMapping.targetField,
            prunedColumns: missingColumns,
            remainingColumnCount: validColumns.length,
          });
        }
      } else {
        // Not enough columns remain - remove the entire mapping
        removed.push({
          targetField: existingMapping.targetField,
          missingColumns:
            missingColumns.length > 0
              ? missingColumns
              : columns.map((c) => ({fileId: c.fileId, columnName: c.name})),
        });
      }
    } else {
      // For NONE, PIVOT_COLUMN, and other single-column transformations, use original logic
      const existingColumns = getTransformationColumnsFromMapping(existingMapping);

      const missingColumns: typeof existingColumns = [];
      for (const column of existingColumns) {
        if (!column) continue;
        if (!columnExistsInExtractedFiles(column, extractedFiles)) {
          missingColumns.push(column);
        }
      }

      if (missingColumns.length > 0) {
        removed.push({
          targetField: existingMapping.targetField,
          missingColumns: missingColumns.map((c) => ({fileId: c.fileId, columnName: c.name})),
        });
      } else {
        retained.push(existingMapping);
      }
    }
  }

  return {removed, pruned, retained};
}

/**
 * Check if a column exists in the extracted files (including pivot columns)
 */
function columnExistsInExtractedFiles(
  column: PayrollFileColumnTransformationColumn,
  extractedFiles: ExtractedFile[],
): boolean {
  // Skip validation for pivot columns - they are virtual columns that don't exist in raw files
  if (column.pivotKeyValue) return true;

  const file = extractedFiles.find((f) => f.file.fileId === column.fileId);
  if (!file) return false;

  // Check both regular columns and pivot columns
  return (
    file.columns.some((c) => c.name === column.name) ||
    (file.pivotColumns && file.pivotColumns.some((c) => c.name === column.name)) ||
    false
  );
}

export function compareFileIds(
  existingMappings: PayrollFileColumnMapping[],
  filesToCompare:
    | {type: 'extracted'; files: ExtractedFile[]}
    | {type: 'base'; files: FileForUpload[]}
    | {type: 'pre-extracted'; files: FileForUpload[]; columnsByFileId: Record<string, string[]>},
) {
  if (existingMappings.length === 0 || filesToCompare.files.length === 0) {
    return {
      missingFileIds: [],
      currentFileIds: [],
      previousFileIds: [],
      suspectedSwappedPairs: [],
    };
  }

  const currentFileIds: Set<string> = new Set();
  const missingFileIds: Set<string> = new Set();
  const previousFileIds: Set<string> = new Set();

  if (filesToCompare.type === 'extracted') {
    for (const file of filesToCompare.files) {
      currentFileIds.add(file.file.fileId);
    }
  } else {
    for (const file of filesToCompare.files) {
      currentFileIds.add(file.fileId);
    }
  }

  for (const mapping of existingMappings) {
    const columns = getTransformationColumnsFromMapping(mapping);
    for (const col of columns) {
      previousFileIds.add(col.fileId);
      if (!currentFileIds.has(col.fileId)) {
        missingFileIds.add(col.fileId);
      }
    }
  }

  // Detect likely swapped IDs when the same set of IDs exists, but columns used
  // by previous mappings appear under a different current fileId.
  const suspectedSwappedPairs: {expectedId: string; actualId: string}[] = [];
  if (missingFileIds.size === 0 && previousFileIds.size > 0 && filesToCompare.type !== 'base') {
    const prevColumnsByFileId: Record<string, Set<string>> = {};
    for (const mapping of existingMappings) {
      const cols = getTransformationColumnsFromMapping(mapping);
      for (const col of cols) {
        if (!col?.fileId || !col?.name) continue;
        if (!prevColumnsByFileId[col.fileId]) {
          prevColumnsByFileId[col.fileId] = new Set();
        }
        prevColumnsByFileId[col.fileId]!.add(col.name);
      }
    }

    const currentColumnsByFileId: Record<string, Set<string>> = {};
    const candidateMap: Record<string, string> = {};
    const usedActuals = new Set<string>();

    if (filesToCompare.type === 'extracted') {
      for (const file of filesToCompare.files) {
        currentColumnsByFileId[file.file.fileId] = new Set((file.columns || []).map((c) => c.name));
      }
    } else if (filesToCompare.type === 'pre-extracted') {
      for (const [fileId, colNames] of Object.entries(filesToCompare.columnsByFileId)) {
        currentColumnsByFileId[fileId] = new Set(colNames || []);
      }
    }

    for (const prevId of Object.keys(prevColumnsByFileId)) {
      const neededCols = prevColumnsByFileId[prevId];
      if (!neededCols || neededCols.size === 0) continue;

      const currentAtPrev = currentColumnsByFileId[prevId];
      if (currentAtPrev) {
        let prevMatches = true;
        for (const colName of neededCols) {
          if (!currentAtPrev.has(colName)) {
            prevMatches = false;
            break;
          }
        }
        if (prevMatches) {
          continue;
        }
      }

      const candidates = Object.keys(currentColumnsByFileId).filter((currId) => {
        if (currId === prevId) return false;
        const currentCols = currentColumnsByFileId[currId];
        for (const colName of neededCols) {
          if (!currentCols.has(colName)) return false;
        }
        return true;
      });

      if (candidates.length === 1 && !usedActuals.has(candidates[0]!)) {
        candidateMap[prevId] = candidates[0]!;
        usedActuals.add(candidates[0]!);
      }
    }

    // Skip pairs that would cause collisions unless the expectedId is also being vacated
    const candidateKeys = new Set(Object.keys(candidateMap));

    for (const [expectedId, actualId] of Object.entries(candidateMap)) {
      const expectedExistsCurrently = currentFileIds.has(expectedId);
      const expectedWillBeVacated = candidateKeys.has(expectedId);
      if (expectedExistsCurrently && !expectedWillBeVacated) {
        continue;
      }
      if (expectedId !== actualId) {
        suspectedSwappedPairs.push({expectedId, actualId});
      }
    }
  }

  return {
    missingFileIds: Array.from(missingFileIds),
    currentFileIds: Array.from(currentFileIds),
    previousFileIds: Array.from(previousFileIds),
    suspectedSwappedPairs,
  };
}

export function applyFileIdSwaps(
  files: FileForUpload[],
  pairs: {actualId: string; expectedId: string}[],
): FileForUpload[] {
  if (!pairs || pairs.length === 0) return files;

  const currentIds = new Set(files.map((f) => f.fileId));
  const remapKeys = new Set(pairs.map((p) => p.actualId));

  const completedPairs: {actualId: string; expectedId: string}[] = [...pairs];
  for (const {actualId, expectedId} of pairs) {
    if (!expectedId || !actualId || expectedId === actualId) continue;
    const reverseExists = pairs.some((p) => p.actualId === expectedId && p.expectedId === actualId);
    if (currentIds.has(expectedId) && !remapKeys.has(expectedId) && !reverseExists) {
      completedPairs.push({actualId: expectedId, expectedId: actualId});
    }
  }

  const allowedRemap: Record<string, string> = {};
  for (const {actualId, expectedId} of completedPairs) {
    if (!expectedId || !actualId || expectedId === actualId) continue;

    const expectedExistsCurrently = currentIds.has(expectedId);
    const expectedWillBeVacated = new Set(completedPairs.map((p) => p.actualId)).has(expectedId);

    if (expectedExistsCurrently && !expectedWillBeVacated) {
      continue;
    }

    allowedRemap[actualId] = expectedId;
  }

  if (Object.keys(allowedRemap).length === 0) return files;

  return files.map((file) => {
    const nextId = allowedRemap[file.fileId];
    return nextId && nextId !== file.fileId ? {...file, fileId: nextId} : file;
  });
}

export function removeInvalidColumnsFromAnchors(
  anchors: PayrollAnchorField[],
  extractedFiles: ExtractedFile[],
): PayrollAnchorField[] {
  if (!anchors || anchors.length === 0 || extractedFiles.length === 0) {
    return anchors || [];
  }

  const validFileIds = new Set(extractedFiles.map((f) => f.file.fileId));
  const columnsByFileId: Record<string, Set<number>> = {};
  for (const f of extractedFiles) {
    columnsByFileId[f.file.fileId] = new Set((f.columns || []).map((c) => c.index));
  }

  return anchors.map((a) => {
    if (a.type !== 'RAW_COLUMNS') return a;
    const cols = (a.columns || []).filter((c) => {
      if (!c?.fileId) return false;
      if (!validFileIds.has(c.fileId)) return false;
      const indices = columnsByFileId[c.fileId];
      return indices ? indices.has(c.index) : false;
    });
    return {...a, columns: cols};
  });
}

export type RemovedMappingInfo = {
  targetField: PayrollTargetField;
  missingColumns: {fileId: string; columnName: string}[];
};

export type PrunedMappingInfo = {
  targetField: PayrollTargetField;
  prunedColumns: {fileId: string; columnName: string}[];
  remainingColumnCount: number;
};
